import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import type { Subscription } from '@etiquetachef/types';
import {
  subscribeAddonConfigs,
  type AddonConfigListResult,
} from '@/lib/firestoreTenantDetail';

type Props = {
  subscription: Subscription | null;
};

/** Reduz o idioma da UI aos 4 locales de `name_i18n` (fr cai em `en`: o catálogo não tem fr). */
function pickLang(lang: string): 'pt-BR' | 'en' | 'es' | 'it' {
  if (lang.startsWith('pt')) return 'pt-BR';
  if (lang.startsWith('es')) return 'es';
  if (lang.startsWith('it')) return 'it';
  return 'en';
}

/**
 * Quadro de add-ons do tenant (aba Cobrança do detalhe). Lista o catálogo e
 * marca cada add-on como "Assinando" (há item em `subscription.items[]` com o
 * mesmo `sku` e `removed_at === null`) ou "Não assina".
 *
 * Origem: sem issue — commit fe63565f0, 2026-06-10 12:08 +0200.
 *
 * Arquitetura: add-ons NÃO viram subscription separada — são itens da mesma
 * `billing/subscription` (`items[]`, ver `addAddonToSubscription` em
 * `apps/functions/src/billing/checkoutSessionCore.ts`). Por isso a fonte aqui é
 * `subscription.items[]`, recebido do pai, não outra leitura.
 *
 * Contrato: assina `/config/addons/items` (`subscribeAddonConfigs`,
 * `lib/firestoreTenantDetail.ts`) enquanto montado; estados LOADING/ERROR/READY.
 * Add-on pausado para venda continua listado — pausar não tira de quem assina.
 *
 * Usado por: `routes/TenantDetailPage.tsx` (rota `/tenants/:id`, aba Cobrança,
 * menu Tenants; spec `docs/screens/tenant-detail-admin.md`), admin web.
 *
 * Dados: `AddonConfig` (`AddonConfigSchema`, `packages/types/src/schemas.ts`),
 * `/config/addons/items/{sku}`, leitura `isSignedIn()` nas rules; `name_i18n`
 * tem só pt-BR/en/es/it. `Subscription.items[]` (`SubscriptionSchema`):
 * `{ sku, origin, amount_cents, removed_at: null | ms }`.
 *
 * Histórico:
 *  - 2026-10-10 14:40 +0200 — (#798) rota, menu e spec (#796).
 */
export function BillingAddonsCard({ subscription }: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<AddonConfigListResult>({ kind: 'LOADING' });

  useEffect(() => {
    setState({ kind: 'LOADING' });
    return subscribeAddonConfigs(setState);
  }, []);

  const lang = pickLang(i18n.language);
  const activeSkus = new Set(
    (subscription?.items ?? [])
      .filter((it) => it.removed_at === null)
      .map((it) => it.sku),
  );

  return (
    <Container
      header={<Header variant="h2">{t('tenantDetail.billing.addons.title')}</Header>}
    >
      {state.kind === 'LOADING' && (
        <Box textAlign="center" padding="m">
          <Spinner />
        </Box>
      )}
      {state.kind === 'ERROR' && (
        <Alert type="error">{t('tenantDetail.billing.addons.errorLoad')}</Alert>
      )}
      {state.kind === 'READY' &&
        (state.addons.length === 0 ? (
          <Box color="text-status-inactive">
            {t('tenantDetail.billing.addons.emptyCatalog')}
          </Box>
        ) : (
          <SpaceBetween size="xs">
            {state.addons.map((addon) => {
              const subscribed = activeSkus.has(addon.sku);
              return (
                <StatusIndicator
                  key={addon.sku}
                  type={subscribed ? 'success' : 'stopped'}
                >
                  {addon.name_i18n[lang]} —{' '}
                  {subscribed
                    ? t('tenantDetail.billing.addons.subscribed')
                    : t('tenantDetail.billing.addons.notSubscribed')}
                </StatusIndicator>
              );
            })}
          </SpaceBetween>
        ))}
    </Container>
  );
}
